import { Add } from "@mui/icons-material";
import {
  Box,
  Typography,
  MenuItem,
  FormControl,
  FormLabel,
  FormControlLabel,
  Switch,
  IconButton,
  useTheme,
  Button,
  TextField,
} from "@mui/material";
import { GridDeleteIcon } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import ColorPicker from "../colorPicker";

const CORES = [
  "Verde",
  "Azul",
  "Amarelo",
  "Laranja",
  "Vermelho",
  "Roxo",
  "Rosa",
  "Cinza",
  "Pastel",
  "Bold",
  "Category",
  "Spectral",
];

interface ColorCustomizationProps {
  styles: any;
  keyName: string[];
  colorValues: string[];
  onColorsChange: (keys: string[], values: string[]) => void;
  customColorsByKey: boolean;
  setCustomColorsByKey: (checked: boolean) => void;
  selectedPalette: string;
  setSelectedPalette: (palette: string) => void;
  getColorsSchema: () => Record<string, any[]>;
  setOpenColorOptionIndex: (index: number | null) => void;
  openColorOptionIndex: number | null;
  getAllColors: () => Record<string, { [key: string]: string | boolean }[]>;
  getColorFromPallet: (index: number) => string;
  handleAddNewColorKey: () => void;
  handleRemoveColorKey: (key: string) => void;
}

const ColorCustomization: React.FC<ColorCustomizationProps> = ({
  styles,
  customColorsByKey,
  setCustomColorsByKey,
  selectedPalette,
  setSelectedPalette,
  getColorsSchema,
  keyName,
  colorValues,
  onColorsChange,
  setOpenColorOptionIndex,
  openColorOptionIndex,
  getAllColors,
  getColorFromPallet,
  handleAddNewColorKey,
  handleRemoveColorKey,
}) => {
  const theme = useTheme();
  const [keys, setKeys] = useState<string[]>(keyName);
  const [colors, setColors] = useState<string[]>(colorValues);

  useEffect(() => {
    setKeys(keyName);
    setColors(colorValues);
  }, [keyName, colorValues]);

  const handleEditKeyName = (index: number, newKey: string) => {
    const updatedKeys = [...keys];
    updatedKeys[index] = newKey;
    setKeys(updatedKeys);
    onColorsChange(updatedKeys, colors);
  };

  const handleEditColorValue = (index: number, newColor: string) => {
    const updatedColors = [...colors];
    updatedColors[index] = newColor;
    setColors(updatedColors);
    onColorsChange(keys, updatedColors);
  };
  return (
    <>
      <Box
        sx={{
          ...styles.container,
          display: "flex",
          flexDirection: "column",
          marginBottom: 2,
          height: "100%",
        }}
      >
        <Typography sx={{ marginBottom: 2 }}>Cores</Typography>
        <TextField
          select
          label="Paleta"
          variant="filled"
          disabled={customColorsByKey}
          value={selectedPalette || "green"}
          onChange={(e) => setSelectedPalette(e.target.value)}
          slotProps={{
            select: {
              MenuProps: {
                anchorOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
              },
            },
          }}
        >
          {Object.entries(getColorsSchema()).map(([key, colors], index) => (
            <MenuItem key={key} value={key}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Typography sx={{ marginRight: 1, flex: 1 }}>
                  {CORES[index] || key}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexShrink: 0,
                  }}
                >
                  {colors.map((color, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 20,
                        height: 20,
                        backgroundColor: color,
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box>
        <Box
          sx={{
            ...styles.container,
            display: "flex",
            alignItems: "end",
            justifyContent: "space-between",
          }}
        >
          <FormControl sx={styles.switchFormControl}>
            <FormLabel
              sx={{
                color: theme.palette.text.primary,
                "&.Mui-focused": {
                  color: theme.palette.text.primary,
                },
              }}
            >
              Customizar
            </FormLabel>
            <FormControlLabel
              value="bottom"
              control={
                <Switch
                  sx={{
                    marginLeft: -1,
                  }}
                  checked={customColorsByKey}
                  onChange={(e) => setCustomColorsByKey(e.target.checked)}
                />
              }
              label="Customiza cores com base nos valores"
              labelPlacement="top"
              sx={styles.switchLabel}
            />
          </FormControl>
          {customColorsByKey && (
            <Button
              sx={{ color: theme.palette.primary.main }}
              startIcon={<Add />}
              onClick={handleAddNewColorKey}
            >
              Adicionar
            </Button>
          )}
        </Box>
        {customColorsByKey &&
          keys.map((key, keyIndex) => (
            <Box
              sx={{
                display: "flex",
                gap: 1,
                position: "relative",
                justifyContent: "center",
                alignItems: "center",
              }}
              key={keyIndex}
            >
              <TextField
                sx={styles.textField}
                label="Nome da coluna"
                variant="filled"
                value={key || ""}
                onChange={(e) => handleEditKeyName(keyIndex, e.target.value)}
              />
              <Box sx={{ display: "flex", marginBottom: 2 }}>
                <ColorPicker
                  colorValue={colors[keyIndex]}
                  keyIndex={keyIndex}
                  colorKey={key}
                  openColorOptionIndex={openColorOptionIndex}
                  setOpenColorOptionIndex={setOpenColorOptionIndex}
                  getAllColors={getAllColors}
                  handleColorPickerSelection={(color) =>
                    handleEditColorValue(keyIndex, color)
                  }
                  getColorFromPallet={getColorFromPallet}
                />
                <IconButton
                  sx={{
                    color: theme.palette.cromatic.red.main,
                  }}
                  onClick={() => handleRemoveColorKey(key)}
                >
                  <GridDeleteIcon />
                </IconButton>
              </Box>
            </Box>
          ))}
      </Box>
    </>
  );
};

export default React.memo(ColorCustomization);
